import React from 'react';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

// core components
import GridContainer from '../md/components/Grid/GridContainer.jsx';
import GridItem from '../md/components/Grid/GridItem.jsx';
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardText from '../md/components/Card/CardText.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import regularFormsStyle from '../assets/jss/crm/views/regularFormsStyle';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumberAndRoles,
  validateEmail,
  validateAcceptablePassword,
  constructPhoneRolesArray,
  showAlert,
} from '../utils/utils';

import CrmTextField from '../components/CrmTextField';
import CrmButton from '../components/CrmButton';
import CrmRadio from '../components/CrmRadio';
import PhoneInput from '../components/PhoneInput';
import AlertModal from '../components/AlertModal';

const registerAccountFormStyles = (theme) => ({
  ...regularFormsStyle,
  radioDiv: {
    transform: 'scale(0.9)',
    height: '32px',
  },
  muiTextField: {
    width: '100%',
  },
  textFieldSupplement: {
    fontSize: '1rem',
  },
  contactInfoHeader: {
    display: 'inline-block',
    marginTop: '40px',
  },
  useAdminCheckboxDiv: {
    display: 'inline-block',
    marginLeft: '40px',
  },
  contactPhoneInputDiv: {
    marginTop: '14px',
  },
  radioButton: {
    padding: '8px 3px 8px 12px',
    '&>span>svg': {
      fontSize: '18px',
    },
  },
  radioButtonSpan: {
    cursor: 'pointer',
  },
  businessTypeLabel: {
    marginTop: '10px',
    marginBottom: '-4px',
    fontSize: '14px',
  },
  textFieldHolder: {
    marginTop: '20px',
  },
});

class RegisterAccountForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      redirectToFirmOverview: false,
      redirectToAccounts: false,
      redirectToLogin: false,

      firmType: 'Treatment Center',
      firmName: '',
      adminFirstName: '',
      adminMiddleNames: '',
      adminLastName: '',
      adminPreferredName: '',
      adminEmail: '',
      password: '',
      confirmPassword: '',
      useAdminAsPrimaryContact: true,
      firmPhoneRoles: ['Business'],
      firmPhoneNumber: '',
      contactPhoneRoles: ['Work', 'Mobile'],
      contactPhoneNumber: '',
      contactFirstName: '',
      contactMiddleNames: '',
      contactLastName: '',
      contactPreferredName: '',
      contactEmail: '',
      contactJobTitle: '',
      contactDescription: '',
    };
  }

  validationPolicy = {
    requireContactJobTitle: true,
    requireContactDescription: false,
    requireAtLeastOneOfContactPhoneOrEmail: false,
    requireContactPhone: false,
    requireContactEmail: true,
  };

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleFirmPhoneRoles = ['Business'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  handleTextChange(event) {
    const id = event.target.id;
    const forbidWhiteSpace =
      id === 'contactEmail' || id === 'adminEmail' || id === 'password' || id === 'confirmPassword';
    const rawValue = event.target.value;
    const value = forbidWhiteSpace ? rawValue.trim() : rawValue;
    this.setState({ [id]: value });
  }

  handleFirmPhoneRolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesArray(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ firmPhoneRoles: newPhoneRoles });
  }

  handleFirmPhoneNumberChange(newNumber) {
    this.setState({ firmPhoneNumber: newNumber });
  }

  handleUseAdminToggle(event) {
    this.setState({ useAdminAsPrimaryContact: event.target.checked });
  }

  handleContactPhoneRolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesArray(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhoneRoles: newPhoneRoles });
  }

  handleContactPhoneNumberChange(newNumber) {
    this.setState({ contactPhoneNumber: newNumber });
  }

  handleContactJobTitleChange(newTitle) {
    this.setState({ contactJobTitle: newTitle });
  }

  handleContactDescriptionChange(newDescrip) {
    this.setState({ contactDescription: newDescrip });
  }

  validateForm() {
    let validation = this.validateCompanyName();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireContactJobTitle) {
      validation = this.validateContactJobTitle();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactDescription) {
      validation = this.validateContactDescription();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactPhone) {
      validation = this.validateContactPhone();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactEmail) {
      validation = this.validateContactEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireAtLeastOneOfContactPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    validation = this.validateUser();
    if (!validation.valid) {
      return validation;
    }

    if (this.supplyingSeparateContact()) {
      validation = this.validateContact();
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  validateCompanyName() {
    if (this.state.firmName.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Account Name' };
    } else if (this.state.firmName.trim().length < 6) {
      return { valid: false, errorMessage: 'You must supply a better Account Name' };
    } else {
      return { valid: true };
    }
  }

  validateContactJobTitle() {
    if (this.state.contactJobTitle.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Job Title for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactDescription() {
    if (this.state.contactDescription.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Description for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactPhone() {
    if (this.supplyingSeparateContact()) {
      if (!this.state.contactPhoneNumber) {
        return { valid: false, errorMessage: 'You must supply a phone number for the Primary Contact' };
      }
      const validation = validatePhoneNumberAndRoles(
        this.state.contactPhoneNumber,
        this.state.contactPhoneRoles,
        'Primary Contact Phone'
      );
      if (!validation.valid) {
        return validation;
      }
    } else {
      if (!this.state.firmPhoneNumber) {
        return { valid: false, errorMessage: "You must supply a 'Sales Line' phone number for the Account" };
      }

      const validation = validatePhoneNumberAndRoles(
        this.state.firmPhoneNumber,
        this.state.firmPhoneRoles,
        'Account Sales Line'
      );
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  validateContactEmail() {
    if (this.supplyingSeparateContact()) {
      if (!this.state.contactEmail) {
        return { valid: false, errorMessage: 'You must supply an email address for the Primary Contact' };
      }
      return validateEmail(this.state.contactEmail, 'Primary Contact Email does not appear to be in a valid format');
    } else {
      if (!this.state.adminEmail) {
        return { valid: false, errorMessage: 'You must supply an email address for the Admin' };
      }
      return validateEmail(this.state.adminEmail, 'Admin Email does not appear to be in a valid format');
    }
  }

  validateContactPhoneOrEmail() {
    if (this.supplyingSeparateContact()) {
      if (this.state.contactEmail || this.state.contactPhoneNumber) {
        if (this.state.contactEmail) {
          return validateEmail(
            this.state.contactEmail,
            "The supplied Primary Contact Email doesn't appear to be valid"
          );
        } else {
          return validatePhoneNumberAndRoles(
            this.state.contactPhoneNumber,
            this.state.contactPhoneRoles,
            'Primary Contact Phone'
          );
        }
      } else {
        return {
          valid: false,
          errorMessage: 'You must supply (at least) either a phone number or an email address for the Primary Contact',
        };
      }
    } else {
      if (this.state.adminEmail || this.state.firmPhoneNumber) {
        if (this.state.adminEmail) {
          return validateEmail(this.state.adminEmail, "The supplied Admin Email doesn't appear to be valid");
        } else {
          return validatePhoneNumberAndRoles(
            this.state.firmPhoneNumber,
            this.state.firmPhoneRoles,
            'Account Sales Line'
          );
        }
      } else {
        return {
          valid: false,
          errorMessage:
            'You must supply (at least) either an Account Sales Line phone number or an email address for the Admin',
        };
      }
    }
  }

  validateUser() {
    let validation = this.validateAdminFirstName();
    if (!validation.valid) {
      return validation;
    }

    validation = this.validateAdminLastName();
    if (!validation.valid) {
      return validation;
    }

    validation = validateEmail(this.state.adminEmail, "The supplied Admin Email doesn't appear to be valid");
    if (!validation.valid) {
      return validation;
    }

    validation = validateAcceptablePassword(this.state.password);
    if (!validation.valid) {
      return validation;
    }

    return this.validatePasswordConfirmation();
  }

  validateAdminFirstName() {
    if (this.state.adminFirstName.trim().length >= 2) {
      return {
        valid: true,
      };
    } else {
      return {
        valid: false,
        errorMessage: 'You need to provide a reasonable First Name for the Admin',
      };
    }
  }

  validateAdminLastName() {
    if (this.state.adminLastName.trim().length >= 2) {
      return {
        valid: true,
      };
    } else {
      return {
        valid: false,
        errorMessage: 'You need to provide a reasonable Last Name for the Admin',
      };
    }
  }

  validatePasswordConfirmation() {
    if (!this.state.confirmPassword) {
      return {
        valid: false,
        errorMessage: "You must enter your password again on the 'Confirm Password' line",
      };
    }
    if (this.state.password !== this.state.confirmPassword) {
      return {
        valid: false,
        errorMessage: "Your password confirmation doesn't match your password",
      };
    }
    return { valid: true };
  }

  validateContact() {
    let validation = this.validateContactFirstName();
    if (!validation.valid) {
      return validation;
    }

    validation = this.validateContactLastName();
    if (!validation.valid) {
      return validation;
    }
    return {
      valid: true,
    };
  }

  validateContactFirstName() {
    if (this.state.contactFirstName.trim().length >= 2) {
      return {
        valid: true,
      };
    } else {
      return {
        valid: false,
        errorMessage: 'You need to provide a reasonable First Name for the Primary Contact',
      };
    }
  }

  validateContactLastName() {
    if (this.state.contactLastName.trim().length >= 2) {
      return {
        valid: true,
      };
    } else {
      return {
        valid: false,
        errorMessage: 'You need to provide a reasonable Last Name for the Primary Contact',
      };
    }
  }

  supplyingSeparateContact() {
    return !this.state.useAdminAsPrimaryContact;
  }

  populateFormWithSampleData() {
    this.setState({
      adminFirstName: 'Florence',
      adminMiddleNames: '',
      adminLastName: 'Nightingale',
      adminPreferredName: 'Flo',
      adminEmail: 'florence.nightingale@gmail.com',
      password: 'P@ssw0rd',
      confirmPassword: 'P@ssw0rd',
      firmName: "Flo's Addiction Recovery, Inc.",
      firmPhoneNumber: '213-444-5678',
      firmPhoneRoles: ['Business'],
      firmStreet: '123 Fourth Street',
      firmCity: 'Anytown',
      firmState: 'CA',
      firmZip: '96002',
      contactFirstName: 'Marcus',
      contactMiddleNames: '',
      contactLastName: 'Welby',
      contactPreferredName: 'Mark',
      contactEmail: 'mark.welby.md@gmail.com',
      contactPhoneNumber: '213-234-5678',
      contactJobTitle: 'Client Advocate',
      contactDescription: 'Helpful to a fault',
    });
  }

  clearForm() {
    this.setState({
      adminFirstName: '',
      adminMiddleNames: '',
      adminLastName: '',
      adminPreferredName: '',
      adminEmail: '',
      password: '',
      confirmPassword: '',
      firmName: '',
      firmPhoneNumber: '',
      firmPhoneRoles: ['Business'],
      firmStreet: '',
      firmCity: '',
      firmState: '',
      firmZip: '',
      contactFirstName: '',
      contactMiddleNames: '',
      contactLastName: '',
      contactPreferredName: '',
      contactEmail: 'com',
      contactPhoneNumber: '',
      contactJobTitle: '',
      contactDescription: '',
    });
  }

  handleSubmit = () => {
    this.setState({ errorMessage: '' });

    const validation = this.validateForm();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }

    const firmApiStruct = {
      firmId: 0,
      firmType: this.state.firmType,
      name: this.state.firmName.trim(),
      salesLine: this.state.firmPhoneNumber,
      contacts: [],
      facilities: [],
      webLinks: [],
    };

    const uri = '/api/firm';
    const request = createStdRequest(uri, 'POST', firmApiStruct);
    const actionOnGoodResponse = (json) => {
      const { firmId } = json;
      if (firmId && firmId > 0) {
        this.addAccountRecordForFirm(firmId);
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  };

  addAccountRecordForFirm(firmId) {
    const uri = `/api/firm/${firmId}/provider`;
    const providerApiStruct = {};
    const request = createStdRequest(uri, 'POST', providerApiStruct);
    const actionOnGoodResponse = (json) => {
      this.createAdminForFirm(firmId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  createAdminForFirm(firmId) {
    const userApiStruct = {
      personId: 0,
      email: this.state.adminEmail,
      firstName: this.state.adminFirstName.trim(),
      middleNames: this.state.adminMiddleNames.trim(),
      lastName: this.state.adminLastName.trim(),
      preferredName: this.state.adminPreferredName.trim(),
      sex: 'Unknown',
      firmId: firmId,
      roles: 'Admin',
      password: this.state.password,
    };

    const uri = '/api/user';
    const request = createStdRequest(uri, 'POST', userApiStruct);
    const actionOnGoodResponse = (json) => {
      const { success, user } = json;
      if (success) {
        const { personId } = user;
        this.createAccountContact(firmId, personId);
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  createAccountContact(firmId, adminId) {
    const contactApiStruct = {
      sex: 'Unknown',
      firmId,
      viewingFirmId: firmId,
      description: this.state.contactDescription.trim(),
      jobTitle: this.state.contactJobTitle.trim(),
    };
    if (this.state.useAdminAsPrimaryContact) {
      contactApiStruct.personId = adminId;
      contactApiStruct.email = this.state.adminEmail;
      contactApiStruct.firstName = this.state.adminFirstName.trim();
      contactApiStruct.middleNames = this.state.adminMiddleNames.trim();
      contactApiStruct.lastName = this.state.adminLastName.trim();
      contactApiStruct.preferredName = this.state.adminPreferredName.trim();
    } else {
      contactApiStruct.personId = 0;
      contactApiStruct.email = this.state.contactEmail;
      contactApiStruct.firstName = this.state.contactFirstName.trim();
      contactApiStruct.middleNames = this.state.contactMiddleNames.trim();
      contactApiStruct.lastName = this.state.contactLastName.trim();
      contactApiStruct.preferredName = this.state.contactPreferredName.trim();
    }
    if (this.state.contactPhoneNumber) {
      const phoneRec = {
        roles: this.state.contactPhoneRoles.join(','),
        countryCode: '1',
        number: this.state.contactPhoneNumber,
      };
      contactApiStruct.phoneNumbers = [phoneRec];
    } else {
      contactApiStruct.phoneNumbers = [];
    }

    const uri = `/api/firm/${firmId}/visibleFirm/${firmId}/contact`;
    const request = createStdRequest(uri, 'POST', contactApiStruct);
    const actionOnGoodResponse = (json) => {
      const { personId } = json;
      if (personId && personId > 0) {
        this.clearForm();
        this.setState({ redirectToAccounts: true });
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleBusinessTypeChange(newValue) {
    this.setState({ firmType: newValue });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.redirectToLogin) {
      return <Redirect to="login" push />;
    }

    if (this.state.redirectToFirmOverview) {
      return <Redirect to="firmOverview" push />;
    }

    if (this.state.redirectToAccounts) {
      return <Redirect to="accounts" push />;
    }

    const { classes } = this.props;
    const onLocalHost = window.location.hostname === 'localhost';

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="allevaOrange" text>
                <CardText color="allevaOrange">
                  <h4 className={classes.cardTitle}>Register Account</h4>
                </CardText>
                <h4 className={classes.cardIconTitle}>
                  Create a new Account and a User who will serve as the Admin for that Account.
                </h4>
              </CardHeader>
              <CardBody>
                <GridContainer>
                  <GridItem xs={12} sm={6}>
                    <h3 className={classes.cardIconTitle}>Account Information</h3>
                    <div className={classes.businessTypeLabel}>Business Type</div>
                    <div className={classes.radioDiv}>
                      <CrmRadio
                        className={classes.radioButton}
                        checked={this.state.firmType === 'Hospital'}
                        onChange={() => this.handleBusinessTypeChange('Hospital')}
                        value="hospital"
                        name="radio-group-firmType"
                      />
                      <span
                        className={classes.radioButtonSpan}
                        onClick={() => this.handleBusinessTypeChange('Hospital')}
                      >
                        Hospital
                      </span>
                      <CrmRadio
                        className={classes.radioButton}
                        checked={this.state.firmType === 'Treatment Center'}
                        onChange={() => this.handleBusinessTypeChange('Treatment Center')}
                        value="treatmentCenter"
                        name="radio-group-firmType"
                      />
                      <span
                        className={classes.radioButtonSpan}
                        onClick={() => this.handleBusinessTypeChange('Treatment Center')}
                      >
                        Treatment Center
                      </span>
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Account Name"
                        id="firmName"
                        required
                        fullWidth
                        value={this.state.firmName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <PhoneInput
                      label="Sales Line"
                      possibleRoles={this.possibleFirmPhoneRoles}
                      actualRoles={this.state.firmPhoneRoles}
                      number={this.state.firmPhoneNumber}
                      onRolesChange={(rolesObj) => this.handleFirmPhoneRolesChange(rolesObj)}
                      onNumberChange={(newNumber) => this.handleFirmPhoneNumberChange(newNumber)}
                    />
                    <h4 className={classes.contactInfoHeader}>Primary Contact</h4>
                    <div className={classes.useAdminCheckboxDiv}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={this.state.useAdminAsPrimaryContact}
                            onChange={(event) => this.handleUseAdminToggle(event)}
                            value="random"
                            color="primary"
                          />
                        }
                        label="Use specified Admin"
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Job Title"
                        id="contactJobTitle"
                        fullWidth
                        value={this.state.contactJobTitle}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Description"
                        id="contactDescription"
                        fullWidth
                        value={this.state.contactDescription}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    {!this.state.useAdminAsPrimaryContact && (
                      <div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="First Name"
                            id="contactFirstName"
                            fullWidth
                            value={this.state.contactFirstName}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Middle Name(s)"
                            id="contactMiddleNames"
                            fullWidth
                            value={this.state.contactMiddleNames}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Last Name"
                            id="contactLastName"
                            fullWidth
                            value={this.state.contactLastName}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Preferred Name"
                            id="contactPreferredName"
                            fullWidth
                            value={this.state.contactPreferredName}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                        <div className={classes.textFieldHolder}>
                          <CrmTextField
                            label="Email Address"
                            id="contactEmail"
                            fullWidth
                            value={this.state.contactEmail}
                            onChange={(event) => this.handleTextChange(event)}
                          />
                        </div>
                      </div>
                    )}
                    <div className={classes.contactPhoneInputDiv}>
                      <PhoneInput
                        label="Contact Phone"
                        possibleRoles={this.possibleContactPhoneRoles}
                        actualRoles={this.state.contactPhoneRoles}
                        number={this.state.contactPhoneNumber}
                        onRolesChange={(rolesObj) => this.handleContactPhoneRolesChange(rolesObj)}
                        onNumberChange={(newNumber) => this.handleContactPhoneNumberChange(newNumber)}
                      />
                    </div>
                  </GridItem>
                  <GridItem xs={12} sm={6}>
                    <h3 className={classes.cardIconTitle}>Define a User to be the Admin</h3>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="First Name"
                        id="adminFirstName"
                        required
                        fullWidth
                        value={this.state.adminFirstName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Middle Name(s)"
                        id="adminMiddleNames"
                        fullWidth
                        value={this.state.adminMiddleNames}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Last Name"
                        id="adminLastName"
                        required
                        fullWidth
                        value={this.state.adminLastName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Preferred Name"
                        id="adminPreferredName"
                        fullWidth
                        value={this.state.adminPreferredName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Email Address"
                        id="adminEmail"
                        required
                        fullWidth
                        value={this.state.adminEmail}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Password"
                        id="password"
                        required
                        fullWidth
                        password
                        value={this.state.password}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                    <div className={classes.textFieldHolder}>
                      <CrmTextField
                        label="Confirm Password"
                        id="confirmPassword"
                        required
                        fullWidth
                        password
                        value={this.state.confirmPassword}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                    </div>
                  </GridItem>
                </GridContainer>
                <GridContainer justifyContent="flex-end">
                  <GridItem xs={12} sm={12} md={12}>
                    <CrmButton filled onClick={() => this.handleSubmit()}>
                      Register Account
                    </CrmButton>
                    {onLocalHost && (
                      <div>
                        <CrmButton outlined onClick={() => this.clearForm()}>
                          Clear Form{' '}
                        </CrmButton>
                        <CrmButton color="secondary" outlined onClick={() => this.populateFormWithSampleData()}>
                          Populate Form with Sample Data
                        </CrmButton>
                      </div>
                    )}
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

export default withStyles(registerAccountFormStyles)(RegisterAccountForm);
